import './offset-calculator.scss';

const INPUT_KEYS = ['a2c', 'ha', 'off', 'wb'];

class OffsetCalculator {
    constructor (element) {
        this.$root = element;
        this.config = this.parseData('offsetCalculatorConfig', {});
        this.i18n = this.parseData('offsetCalculatorI18n', {});

        this.$trailKnown = this.$root.querySelector('[data-offset-calculator-trail]');
        this.$trailCalc = this.out('trailCalc');
        this.$options = this.$root.querySelector('[data-offset-calculator-options]');
    }

    parseData (key, fallback) {
        try {
            return JSON.parse(this.$root.dataset[key]);
        } catch (e) {
            return fallback;
        }
    }

    out (name) {
        return this.$root.querySelector(`[data-offset-calculator-out="${name}"]`);
    }

    // translate with %placeholder% replacement (Symfony/shared-translation syntax)
    t (key, params = {}) {
        let text = this.i18n[key] ?? key;

        Object.keys(params).forEach((placeholder) => {
            text = text.split(placeholder).join(params[placeholder]);
        });

        return text;
    }

    initialize () {
        INPUT_KEYS.forEach((key) => this.sync(key));

        if (this.$trailKnown) {
            this.$trailKnown.addEventListener('input', () => this.recalc());
        }

        this.recalc();
    }

    static rad (deg) {
        return (deg * Math.PI) / 180;
    }

    static deg (rad) {
        return (rad * 180) / Math.PI;
    }

    trailFn (headAngle, offset) {
        const radius = Number(this.config.tireRadius);

        return (radius * Math.cos(OffsetCalculator.rad(headAngle)) - offset) / Math.sin(OffsetCalculator.rad(headAngle));
    }

    static clamp (value, min, max) {
        return Math.max(min, Math.min(max, value));
    }

    // number formatting: decimal point (per DT Swiss design)
    static formatNumber (value, digits) {
        return value.toFixed(digits);
    }

    static fmt (value, digits) {
        return `${value >= 0 ? '+' : '−'}${OffsetCalculator.formatNumber(Math.abs(value), digits)}`;
    }

    static paintRange ($range) {
        const min = parseFloat($range.min);
        const max = parseFloat($range.max);
        const value = parseFloat($range.value);

        $range.style.setProperty('--offset-calculator-range-progress', `${((value - min) / (max - min)) * 100}%`);
    }

    sync (key) {
        const $range = this.$root.querySelector(`[data-offset-calculator-range="${key}"]`);
        const $number = this.$root.querySelector(`[data-offset-calculator-number="${key}"]`);

        if (!$range || !$number) {
            return;
        }

        $range.addEventListener('input', () => {
            $number.value = $range.value;
            OffsetCalculator.paintRange($range);
            this.recalc();
        });

        $number.addEventListener('input', () => {
            const value = parseFloat($number.value);

            if (!isNaN(value)) {
                $range.value = OffsetCalculator.clamp(value, parseFloat($range.min), parseFloat($range.max));
                OffsetCalculator.paintRange($range);
                this.recalc();
            }
        });

        $number.addEventListener('blur', () => {
            const value = OffsetCalculator.clamp(parseFloat($number.value) || parseFloat($range.value), parseFloat($range.min), parseFloat($range.max));
            $number.value = value;
            $range.value = value;
            OffsetCalculator.paintRange($range);
            this.recalc();
        });

        OffsetCalculator.paintRange($range);
    }

    value (key) {
        const $number = this.$root.querySelector(`[data-offset-calculator-number="${key}"]`);

        return parseFloat($number.value);
    }

    recalc () {
        const a2c = this.value('a2c');
        const ha0 = this.value('ha');
        const off0 = this.value('off');
        const wb0 = this.value('wb');

        if ([a2c, ha0, off0, wb0].some(isNaN)) {
            return;
        }

        const unit = this.t('unitMm');

        const effA2C = Number(this.config.f132Static);
        const dA2C = effA2C - a2c;
        const dHAdeg = OffsetCalculator.deg((dA2C * Math.sin(OffsetCalculator.rad(ha0))) / wb0);
        const newHA = ha0 - dHAdeg;

        const origH = a2c * Math.cos(OffsetCalculator.rad(ha0)) + off0 * Math.sin(OffsetCalculator.rad(ha0));
        const origV = a2c * Math.sin(OffsetCalculator.rad(ha0)) - off0 * Math.cos(OffsetCalculator.rad(ha0));

        const calcTrail = this.trailFn(ha0, off0);
        const rawKnown = this.$trailKnown ? this.$trailKnown.value.trim() : '';
        const knownValue = parseFloat(rawKnown);
        const useKnown = rawKnown !== '' && !isNaN(knownValue) && knownValue > 0;
        const t0 = useKnown ? knownValue : calcTrail;

        if (this.$trailKnown) {
            this.$trailKnown.classList.toggle('offset-calculator__number--override', useKnown);
        }

        if (this.$trailCalc) {
            this.$trailCalc.textContent = this.t('trailCalculated', { '%value%': OffsetCalculator.formatNumber(calcTrail, 1) });
        }

        this.setPreview(unit, dA2C, dHAdeg, newHA, t0, useKnown);
        this.renderOptions(effA2C, newHA, off0, origH, origV, wb0, t0, unit);
    }

    setPreview (unit, dA2C, dHAdeg, newHA, t0, useKnown) {
        const $da2c = this.out('da2c');
        const $da2cSub = this.out('da2cSub');
        const $ha = this.out('headangle');
        const $haSub = this.out('headangleSub');
        const $trailRef = this.out('trailRef');
        const $trailRefSrc = this.out('trailRefSrc');

        if ($da2c) {
            $da2c.textContent = `${dA2C >= 0 ? '+' : '−'}${Math.abs(dA2C)} ${unit}`;
        }

        if ($da2cSub) {
            $da2cSub.textContent = this.t('da2cSub');
        }

        if ($ha) {
            $ha.textContent = `${OffsetCalculator.formatNumber(newHA, 2)}°`;
        }

        if ($haSub) {
            const dHAabs = Math.abs(dHAdeg);

            if (dHAabs < 0.01) {
                $haSub.textContent = this.t('headangleUnchanged');
            } else {
                const key = dHAdeg > 0 ? 'headangleFlatter' : 'headangleSteeper';
                $haSub.textContent = this.t(key, { '%deg%': OffsetCalculator.formatNumber(dHAabs, 2) });
            }
        }

        if ($trailRef) {
            $trailRef.textContent = `${OffsetCalculator.formatNumber(t0, 1)} ${unit}`;
            $trailRef.classList.toggle('offset-calculator__preview-value--override', useKnown);
        }

        if ($trailRefSrc) {
            $trailRefSrc.textContent = useKnown ? this.t('trailSrcInput') : this.t('trailSrcCalc');
        }
    }

    renderOptions (effA2C, newHA, off0, origH, origV, wb0, t0, unit) {
        if (!this.$options) {
            return;
        }

        const offsets = (this.config.offsets || []).map(Number);
        const matchThreshold = Number(this.config.thresholds.match);
        const strongThreshold = Number(this.config.thresholds.strong);

        const results = offsets.map((off) => {
            const nH = effA2C * Math.cos(OffsetCalculator.rad(newHA)) + off * Math.sin(OffsetCalculator.rad(newHA));
            const nV = effA2C * Math.sin(OffsetCalculator.rad(newHA)) - off * Math.cos(OffsetCalculator.rad(newHA));
            const trail = this.trailFn(newHA, off);

            return {
                offset: off,
                trail,
                dTrail: trail - t0,
                wb: wb0 + (nH - origH),
                dWB: nH - origH,
                rise: nV - origV,
            };
        });

        let bestIdx = 0;

        for (let i = 1; i < results.length; i++) {
            if (Math.abs(results[i].dTrail) < Math.abs(results[bestIdx].dTrail)) {
                bestIdx = i;
            }
        }

        this.$options.innerHTML = results.map((result, index) => this.optionMarkup(result, index === bestIdx, matchThreshold, strongThreshold, unit)).join('');
    }

    optionMarkup (result, isBest, matchThreshold, strongThreshold, unit) {
        let deltaClass = '';
        let feel;
        let feelMatch = false;

        if (Math.abs(result.dTrail) < matchThreshold) {
            deltaClass = ' offset-calculator__option-row-delta--match';
            feel = this.t('feelMatch');
            feelMatch = true;
        } else if (result.dTrail > 0) {
            feel = result.dTrail > strongThreshold ? this.t('feelStableStrong') : this.t('feelStable');
        } else {
            feel = result.dTrail < -strongThreshold ? this.t('feelLivelyStrong') : this.t('feelLively');
        }

        const tag = isBest ? `<span class="offset-calculator__option-tag">${this.t('recommendation')}</span>` : '';

        return `<div class="offset-calculator__option${isBest ? ' offset-calculator__option--best' : ''}">${tag}<div class="offset-calculator__option-offset">${result.offset} ${unit}</div><div class="offset-calculator__option-rows"><div class="offset-calculator__option-row"><span class="offset-calculator__option-row-label">${this.t('optTrail')}</span><div class="offset-calculator__option-row-values"><span class="offset-calculator__option-row-main">${OffsetCalculator.formatNumber(result.trail, 0)} ${unit}</span><span class="offset-calculator__option-row-delta${deltaClass}">${OffsetCalculator.fmt(result.dTrail, 1)} ${unit}</span></div></div><div class="offset-calculator__option-row"><span class="offset-calculator__option-row-label">${this.t('optWheelbase')}</span><div class="offset-calculator__option-row-values"><span class="offset-calculator__option-row-main">${OffsetCalculator.formatNumber(result.wb, 0)} ${unit}</span><span class="offset-calculator__option-row-delta">${OffsetCalculator.fmt(result.dWB, 0)} ${unit}</span></div></div><div class="offset-calculator__option-row"><span class="offset-calculator__option-row-label">${this.t('optRise')}</span><div class="offset-calculator__option-row-values"><span class="offset-calculator__option-row-main">${OffsetCalculator.fmt(result.rise, 1)} ${unit}</span><span class="offset-calculator__option-row-delta">${this.t('optRiseVs')}</span></div></div></div><div class="offset-calculator__option-feel${feelMatch ? ' offset-calculator__option-feel--match' : ''}">${feel}</div></div>`;
    }
}

// iframe embed: report size to the host so it can resize the iframe (auto-height)
let embedMessengerReady = false;

const postEmbedSize = () => {
    const $documentElement = document.documentElement;

    window.parent.postMessage({
        type: 'offset-calculator:resize',
        width: $documentElement.scrollWidth,
        height: $documentElement.scrollHeight,
    }, '*');
};

const setupEmbedMessenger = () => {
    // only relevant when actually embedded in another window
    if (embedMessengerReady || window.parent === window) {
        return;
    }

    embedMessengerReady = true;

    // debounce via setTimeout, not requestAnimationFrame: browsers suspend rAF in
    // iframes they are not actively painting, which would stop the size messages
    let timer = null;
    const schedule = () => {
        if (timer) {
            return;
        }

        timer = window.setTimeout(() => {
            timer = null;
            postEmbedSize();
        }, 100);
    };

    if ('ResizeObserver' in window) {
        new ResizeObserver(schedule).observe(document.documentElement);
    }

    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    schedule();
};

const initOffsetCalculators = (context) => {
    if (!context) {
        return;
    }

    let found = false;

    context.querySelectorAll('[data-offset-calculator="root"]').forEach(($root) => {
        found = true;

        if ($root.dataset.offsetCalculatorReady === 'true') {
            return;
        }

        $root.dataset.offsetCalculatorReady = 'true';
        new OffsetCalculator($root).initialize();
    });

    if (found) {
        setupEmbedMessenger();
    }
};

export { OffsetCalculator };

window.addEventListener('content.loaded', (e) => {
    initOffsetCalculators(e.detail.$context);
});
